import { createFileRoute } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import z from "zod";

import { QueryBuilderContext } from "@/contexts/query-builder-context";
import { useIntegrations } from "@/hooks/use-integrations";
import { useTableQueryBuilder } from "@/hooks/use-table-query-builder";
import type { PersonifyEventLog } from "@/types/integrations";
import { PaginatedTable } from "@/ui/common/paginated-table";
import { HeaderSearchInput } from "@/ui/table/header-search-input";
import { formatShortDate } from "@/utils";

const columns: ColumnDef<PersonifyEventLog>[] = [
  { id: "id", accessorKey: "id" },
  {
    id: "createdAt",
    accessorKey: "createdAt",
    header: "When",
    cell: ({ row }) => {
      return formatShortDate(row.original.createdAt);
    },
  },
  {
    id: "eventType",
    accessorKey: "eventType",
    header: (header) => {
      return <HeaderSearchInput header={header} name="Event type" />;
    },
    enableSorting: false,
  },
  {
    id: "externalMemberId",
    accessorKey: "externalMemberId",
    header: (header) => {
      return <HeaderSearchInput header={header} name="External Member ID" />;
    },
    cell: ({ row }) => {
      return row.original.externalMemberId ?? "—";
    },
    enableSorting: false,
  },
  {
    id: "trigger",
    accessorKey: "trigger",
    header: "Trigger",
    cell: ({ row }) => {
      return row.original.trigger ?? "—";
    },
    enableSorting: false,
  },
  {
    id: "userId",
    accessorKey: "userId",
    header: "User ID",
    cell: ({ row }) => {
      return row.original.userId ?? "—";
    },
    enableSorting: false,
  },
];

const PERSONIFY_EVENT_FILTER_KEYS = ["eventType", "externalMemberId"] as const;

const PersonifyEventsPage = () => {
  const { queryBuilder, setCurrentPage, setSorting, sortingState } = useTableQueryBuilder({
    filterKeys: PERSONIFY_EVENT_FILTER_KEYS,
  });

  const { data: events, isLoading } = useIntegrations().getPersonifyEventLog({ queryBuilder });

  return (
    <QueryBuilderContext.Provider value={{ builder: queryBuilder }}>
      <PaginatedTable
        columns={columns}
        data={events?.data}
        isLoadingData={isLoading}
        pagination={events?.pagination}
        setCurrentPage={setCurrentPage}
        setSorting={setSorting}
        sorting={sortingState}
      />
    </QueryBuilderContext.Provider>
  );
};

export const Route = createFileRoute("/_private/integrations/personify/events/")({
  component: PersonifyEventsPage,
  validateSearch: z.object({
    page: z.number().optional(),
    ...columns.reduce(
      (acc: Record<string, z.ZodOptional<z.ZodUnion<[z.ZodString, z.ZodNumber]>>>, column) => {
        if (column.id) {
          acc[column.id] = z.string().or(z.number().int()).optional();
        }

        return acc;
      },
      {},
    ),
  }),
});
